
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { Form } from 'element-ui'
import moment from 'moment'

import { MultiChoiceCard } from '@/models'
import { extractApiErrorMessages } from '@/utils/apiErrors'

@Component({
  name: 'ExtendMultiChoiceCardsExpirationDialog'
})
export default class extends Vue {
  @Prop({ required: true }) private visible!: boolean
  @Prop({ required: true }) private cards!: MultiChoiceCard[]

  private form = { duration: 30 }
  private loading = false

  get isBulk(): boolean {
    return this.cards.length > 1
  }

  get rules(): any {
    return {
      duration: [
        { required: true, message: String(this.$t('gifts.extendDialog.durationRequired')), trigger: 'blur' }
      ]
    }
  }

  get dialogTitle(): string {
    return this.isBulk
      ? String(this.$t('gifts.extendBulkDialog.title'))
      : String(this.$t('gifts.extendDialog.title'))
  }

  get confirmTitle(): string {
    if (this.isBulk) {
      return String(this.$t('gifts.extendBulkDialog.confirmTitle', {
        count: this.cards.length,
        days: this.form.duration
      }))
    }
    return String(this.$t('gifts.extendDialog.confirmTitle', { days: this.form.duration }))
  }

  get activeCards(): MultiChoiceCard[] {
    return this.cards.filter(card => card.isActiveForExtension)
  }

  get expiredCards(): MultiChoiceCard[] {
    return this.cards.filter(card => !card.isActiveForExtension)
  }

  @Watch('visible')
  private onVisibleChange(visible: boolean) {
    if (visible) {
      this.form.duration = 30
      this.$nextTick(() => {
        (this.$refs.extendForm as Form)?.clearValidate()
      })
    }
  }

  formatDate(date: string | Date | null | undefined): string {
    if (!date) return ''
    const formattedDate = moment(date).format('L')
    return formattedDate === 'Invalid date' ? '' : formattedDate
  }

  private notifyErrors(err: any): void {
    const messages = extractApiErrorMessages(err)
    const fallback = String(this.$t('gifts.extendDialog.notification.error.fallback'))
    const toDisplay = messages.length > 0 ? messages : [fallback]

    for (const message of toDisplay) {
      this.$notify({
        title: String(this.$t('gifts.extendDialog.notification.error.title')),
        message,
        type: 'error',
        duration: 5000
      })
    }
  }

  submit() {
    (this.$refs.extendForm as Form).validate(async(valid) => {
      if (!valid || this.loading) return

      this.loading = true
      try {
        if (this.isBulk) {
          await MultiChoiceCard.extendDurations(
            this.form.duration,
            this.cards.map(card => card.id)
          )
          this.$notify({
            title: String(this.$t('gifts.extendDialog.notification.success.title')),
            message: String(this.$t('gifts.extendBulkDialog.notification.success.message', {
              count: this.cards.length,
              days: this.form.duration
            })),
            type: 'success',
            duration: 2000
          })
          this.$emit('extended')
        } else {
          const updated = await this.cards[0].extendExpiration(this.form.duration)
          this.$notify({
            title: String(this.$t('gifts.extendDialog.notification.success.title')),
            message: String(this.$t('gifts.extendDialog.notification.success.message', { days: this.form.duration })),
            type: 'success',
            duration: 2000
          })
          this.$emit('extended', [updated])
        }
        this.$emit('close')
      } catch (err: any) {
        this.notifyErrors(err)
      } finally {
        this.loading = false
      }
    })
  }
}
